import { ShieldAlert } from "lucide-react";
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Alert } from "./alert";
import { Button } from "./button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "./dialog";
import { Field } from "./form";
import { Input } from "./input";

/**
 * RE-AUTHENTICATION BEFORE A SENSITIVE ACTION.
 *
 * A handful of changes are worth more to an attacker than everything around
 * them: the bank account printed on invoices, the gateway keys that decide
 * where clients' money lands, an API key that outlives a password change, a
 * promotion to administrator, a full export of the workspace. A stolen session
 * cookie is enough for all of them, and none of them looks unusual in a log.
 *
 * So the server asks for the password again, and this is how the browser
 * collects it. Deliberately shaped like `useConfirm`, because it is the same
 * kind of interruption and should read the same way at the call site:
 *
 *     const stepUp = useStepUp();
 *     const proof = await stepUp({ title: "Change the bank details?", … });
 *     if (!proof) return;                       // they cancelled
 *     save.mutate({ ...values, ...proof });     // { password, totpCode? }
 *
 * The password is handed to the caller and never stored: it lives in this
 * component's state for as long as the dialog is open and is cleared when it
 * closes, including on cancel.
 *
 * The two-factor code is asked for only when the server says it is needed —
 * `retry` re-opens the dialog with a message and the TOTP field showing, which
 * is how a 401 TWO_FACTOR_REQUIRED becomes a second prompt rather than a dead
 * end.
 */

export interface StepUpOptions {
  title: ReactNode;
  /** What is about to change, and why it is worth checking. */
  description?: ReactNode;
  confirmLabel?: string;
  /** Show the authenticator field straight away (the account has 2FA on). */
  needsTotp?: boolean;
  /** Shown as an error above the fields — e.g. after a rejected attempt. */
  error?: string;
}

export interface StepUpProof {
  password: string;
  totpCode?: string;
}

type StepUpFn = (options: StepUpOptions) => Promise<StepUpProof | null>;
const StepUpContext = createContext<StepUpFn | null>(null);

export function useStepUp(): StepUpFn {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error("useStepUp must be used inside <StepUpProvider>");
  return ctx;
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(StepUpOptions & { open: boolean }) | null>(null);
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const resolver = useRef<((v: StepUpProof | null) => void) | null>(null);

  const stepUp = useCallback<StepUpFn>((options) => {
    // A second prompt while one is open cancels the first rather than losing it.
    resolver.current?.(null);
    setPassword("");
    setTotpCode("");
    setState({ ...options, open: true });
    return new Promise<StepUpProof | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (result: StepUpProof | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setState((s) => (s ? { ...s, open: false } : s));
    // Never leave a password in memory behind a closed dialog.
    setPassword("");
    setTotpCode("");
  };

  const submit = () => {
    if (!password) return;
    close({ password, ...(totpCode.trim() ? { totpCode: totpCode.trim() } : {}) });
  };

  return (
    <StepUpContext.Provider value={stepUp}>
      {children}
      <Dialog open={!!state?.open} onOpenChange={(o) => !o && close(null)}>
        <DialogContent>
          <DialogHeader
            title={state?.title ?? "Confirm it is you"}
            description={state?.description}
            icon={<ShieldAlert className="size-5" />}
          />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <DialogBody className="grid gap-4">
              {state?.error && <Alert tone="danger">{state.error}</Alert>}

              <Field label="Your password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                />
              </Field>

              {state?.needsTotp && (
                <Field label="Authentication code" description="The 6-digit code from your authenticator app.">
                  <Input
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    inputMode="numeric"
                    maxLength={6}
                    className="text-center font-mono text-lg tracking-[0.4em]"
                  />
                </Field>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => close(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!password}>
                {state?.confirmLabel ?? "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </StepUpContext.Provider>
  );
}
